import { useEffect } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { signInWithPopup, signOut } from 'firebase/auth'
import { auth, googleProvider } from '@/lib/firebase'
import { useAuthStore } from '@/store/authStore'
import { useThemeStore } from '@/store/themeStore'
import { cn } from '@/lib/utils'
import {
  Clock,
  Users,
  Settings,
  LogIn,
  LogOut,
  User,
  Sun,
  Moon,
  BarChart3,
} from 'lucide-react'

const primaryNav = [
  { path: '/', label: 'Sangam', icon: Users },
  { path: '/recordings', label: 'Archive', icon: Clock },
]

export default function Layout() {
  const location = useLocation()
  const { user, isLoading, isAdmin, logout } = useAuthStore()
  const { isDarkMode, toggleTheme } = useThemeStore()

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [isDarkMode])

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (error) {
      console.error('Sign in error:', error)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth)
      logout()
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-strategic-light dark:bg-gray-950 transition-colors duration-300">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-strategic-slate/10 dark:border-gray-800 transition-colors duration-300 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center overflow-hidden border border-white/20 dark:border-gray-700/50 group-hover:scale-105 transition-transform">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="font-display text-lg font-bold text-gray-900 dark:text-white leading-tight truncate">
                  Strategic Sangam
                </h1>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-none font-medium">
                  Multi-Agent Podcast
                </p>
              </div>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {primaryNav.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={cn(
                    'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    location.pathname === path
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </Link>
              ))}

              {/* Admin links */}
              {isAdmin() && (
                <div className="flex items-center border-l border-gray-200 dark:border-gray-800 ml-2 pl-2 space-x-1">
                  <Link
                    to="/metrics"
                    className={cn(
                      'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      location.pathname === '/metrics'
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                    )}
                    title="Metrics"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Metrics</span>
                  </Link>
                  <Link
                    to="/admin"
                    className={cn(
                      'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      location.pathname === '/admin'
                        ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                    )}
                    title="Admin"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Admin</span>
                  </Link>
                </div>
              )}
            </nav>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Auth */}
              {isLoading ? (
                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse" />
              ) : user ? (
                <div className="flex items-center space-x-3">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || 'User'}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                  )}
                  <span className="hidden lg:inline text-sm text-gray-700 dark:text-gray-300">
                    {user.displayName || user.email}
                  </span>
                  <button
                    onClick={handleSignOut}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    title="Sign out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign In</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 z-50 transition-colors duration-300">
        <div className="flex justify-around py-2">
          {primaryNav.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              className={cn(
                'flex flex-col items-center py-1 px-3',
                location.pathname === path
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 dark:text-gray-400'
              )}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] mt-1 uppercase font-bold tracking-tighter">{label}</span>
            </Link>
          ))}
          {isAdmin() && (
            <>
              <Link
                to="/metrics"
                className={cn(
                  'flex flex-col items-center py-1 px-3',
                  location.pathname === '/metrics'
                    ? 'text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                <BarChart3 className="w-5 h-5" />
                <span className="text-[10px] mt-1 uppercase font-bold tracking-tighter">Metrics</span>
              </Link>
              <Link
                to="/admin"
                className={cn(
                  'flex flex-col items-center py-1 px-3',
                  location.pathname === '/admin'
                    ? 'text-accent-600 dark:text-accent-400'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                <Settings className="w-5 h-5" />
                <span className="text-[10px] mt-1 uppercase font-bold tracking-tighter">Admin</span>
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="hidden md:block bg-white dark:bg-gray-900 border-t border-strategic-slate/10 dark:border-gray-800 py-6 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
            <p>Strategic Sangam - Multi-Agent Podcast Platform</p>
            <p>© {new Date().getFullYear()} Nexus Agent</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
